import { useCallback } from 'react';

import { FilterOperator, FiltersState } from 'framework-listing-lib/types';
import {
  useComputedFilters,
  useFiltersStateStore,
} from 'framework-listing-lib/internal/providers/CrmObjectTypeListingClientProvider';
import {
  getFilterValueByProperty,
  updateFilterState,
} from 'framework-listing-lib/utils/filters';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';

export default function useQuickFilters() {
  'use memo';
  return useComputedFilters().quickFilters;
}

export function useQuickFiltersCount() {
  'use memo';
  const quickFilters = useQuickFilters();
  return quickFilters.length;
}

export function useSetQuickFilter() {
  'use memo';
  const { trackCrmTableInteraction } = useCrmObjectTypeTracking();
  const onSetFilters = useFiltersStateStore((state) => state.setFilters);

  return useCallback(
    (filterOperator: FilterOperator) => {
      trackCrmTableInteraction({
        action: 'add-quick-filter',
        property: filterOperator.property,
      });

      onSetFilters((prev) =>
        updateFilterState(prev, {
          quickFilters: [
            ...prev.quickFilters.filter(
              (filter) => filter.property !== filterOperator.property
            ),
            filterOperator,
          ],
        })
      );
    },
    [onSetFilters, trackCrmTableInteraction]
  );
}

export function useRemoveQuickFilter() {
  'use memo';
  const { trackCrmTableInteraction } = useCrmObjectTypeTracking();
  const onSetFilters = useFiltersStateStore((state) => state.setFilters);

  return useCallback(
    (propertyName: string) => {
      trackCrmTableInteraction({
        action: 'remove-quick-filter',
        property: propertyName,
      });

      onSetFilters((prev) => ({
        ...prev,
        quickFilters: [
          ...prev.quickFilters.filter(
            (filter) => filter.property !== propertyName
          ),
        ],
      }));
    },
    [onSetFilters, trackCrmTableInteraction]
  );
}

export function useGetQuickFilterValue() {
  'use memo';
  const quickFilters = useQuickFilters();

  return useCallback(
    (propertyName: string, defaultValue: any = null) => {
      return getFilterValueByProperty(
        {
          filterGroups: [{ filters: quickFilters }],
        },
        propertyName,
        defaultValue
      );
    },
    [quickFilters]
  );
}

export function useSetQuickFilters() {
  'use memo';
  const { trackCrmTableInteraction } = useCrmObjectTypeTracking();
  const onSetFilters = useFiltersStateStore((state) => state.setFilters);

  return useCallback(
    (quickFilters: FiltersState['quickFilters']) => {
      trackCrmTableInteraction({
        action: 'set-quick-filters',
      });

      onSetFilters((prev) =>
        updateFilterState(prev, {
          quickFilters,
        })
      );
    },
    [onSetFilters, trackCrmTableInteraction]
  );
}

export function useListingLibQuickFilters() {
  'use memo';
  const quickFilters = useQuickFilters();
  const getQuickFilterValue = useGetQuickFilterValue();
  const setQuickFilter = useSetQuickFilter();
  const removeQuickFilter = useRemoveQuickFilter();
  const setQuickFilters = useSetQuickFilters();

  return {
    getQuickFilterValue,
    quickFilters,
    removeQuickFilter,
    setQuickFilter,
    setQuickFilters,
  };
}
